import { useEffect, useRef, useState } from 'react';
import { resolveLeadForm } from '../../lib/resolveLeadForm';
import type { AccountCampaign } from '../../types/accountCampaign';
import type { Campaign } from '../../types/campaign';

// Multi Lead Form spec §3.1 + §3.3. Master grid cell for the Lead Form
// column. Three rendering states:
//
//   - Unassigned / opt-out / no Campaign data → muted em-dash. Lead form
//     concept doesn't apply (no Campaign to pick from). Locked.
//
//   - Single-form Campaign → static text showing the form name. No
//     dropdown because there's nothing to pick. Status pill style for
//     "you can't change this here" affordance.
//
//   - Multi-form Campaign, leadFormId is OK → click opens a dropdown of
//     all current campaign.campaignLeadForms entries; picking one calls
//     onChange with the target id. Server re-snapshots row.leadForm.
//
//   - Orphan (leadFormId no longer in campaign.campaignLeadForms) → red
//     pill with "⚠ Reassign". Click opens the same dropdown so the AM
//     can pick a current entry. Title attribute carries the warning text
//     for accessibility / hover.

type Props = {
  row: AccountCampaign;
  campaign: Campaign | null;
  disabled: boolean;
  busy: boolean;
  onChange: (leadFormId: string) => void;
  ariaLabel?: string;
};

export function LeadFormCell({
  row,
  campaign,
  disabled,
  busy,
  onChange,
  ariaLabel,
}: Props) {
  const resolved = resolveLeadForm(row, campaign);
  const list = campaign?.campaignLeadForms ?? [];
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement | null>(null);

  // Outside-click to close (simple inline since this is the only consumer
  // and our shared useClickOutside helper expects a ref the parent owns).
  useEffect(() => {
    if (!open) return;
    const onDocClick = (e: MouseEvent) => {
      if (!wrapRef.current) return;
      if (!wrapRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [open]);

  if (resolved.state === 'unassigned' || !campaign) {
    return <span className="text-slate-400">—</span>;
  }

  // Single-form Campaign: nothing to pick. Render the form name as
  // static, non-interactive text. (Spec §3.1: "When row's Campaign has
  // only one lead form, the column shows the form name as static text".)
  if (list.length === 1) {
    return (
      <span
        className="text-sm text-slate-700"
        title={`This Campaign has one lead form ("${list[0]!.name}"). Add more in the Campaign editor.`}
      >
        {list[0]!.name}
      </span>
    );
  }

  const pick = (id: string) => {
    setOpen(false);
    if (id === row.leadFormId) return;
    onChange(id);
  };

  const orphan = resolved.state === 'orphan';
  const displayName = resolved.leadForm?.name ?? null;

  return (
    <div className="relative inline-block" ref={wrapRef}>
      <button
        type="button"
        aria-label={ariaLabel ?? 'Select lead form'}
        aria-haspopup="listbox"
        aria-expanded={open}
        disabled={disabled || busy}
        onClick={() => setOpen((v) => !v)}
        title={
          orphan
            ? "This row's lead form was deleted from the Campaign. Pick a new one."
            : undefined
        }
        className={`text-xs px-2 py-1 rounded-md border ${
          orphan
            ? 'border-red-300 bg-red-50 text-red-700 hover:bg-red-100'
            : 'border-slate-300 bg-white text-slate-700 hover:bg-slate-50'
        } disabled:opacity-50 disabled:cursor-not-allowed`}
      >
        {orphan ? '⚠ Reassign' : (displayName ?? '—')}
      </button>
      {open && (
        <div
          role="listbox"
          className="absolute left-0 z-10 mt-1 min-w-[10rem] rounded-md border border-slate-200 bg-white py-1 shadow-md"
        >
          {list.map((lf) => {
            const selected = lf.id === row.leadFormId;
            return (
              <button
                key={lf.id}
                type="button"
                role="option"
                aria-selected={selected}
                onClick={() => pick(lf.id)}
                className={`block w-full px-3 py-1.5 text-left text-xs hover:bg-slate-50 ${
                  selected ? 'font-semibold text-slate-900' : 'text-slate-700'
                }`}
              >
                <span>{lf.name}</span>
                {lf.id === campaign.defaultLeadFormId && (
                  <span className="ml-2 inline-flex items-center rounded-full bg-slate-200 px-1.5 py-0.5 text-[9px] uppercase tracking-wide text-slate-700">
                    default
                  </span>
                )}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
